// Onbekend afzenderdomein koppelen (planner), met de markup van de koppellade in het prototype (KOP_DRAWER in
// design/build/prototype.js): eerst de lijst van onbekende domeinen, dan per domein een klant kiezen of negeren
// (platform of leverancier). De keuze geldt ook voor wat al binnen is: het overzicht rekent opnieuw. Met naam en tijd in
// het logboek, en Ongedaan maken in de melding.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { supportApi, type UnknownDomain } from "../lib/api-support";
import { Icon } from "../lib/icons";
import { nOf, suggestKlant, when } from "../lib/support";
import type { Klant } from "../lib/types";
import { useToast } from "../shell/Toast";

const IGNORE = "__geen";

export function DomainLinkDrawer({ start, unknown, klanten, onClose }: {
  /** null: de lijst; een domein: meteen de klant kiezen */
  start: string | null;
  unknown: UnknownDomain[];
  klanten: Klant[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [domain, setDomain] = useState<string | null>(start);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string[]>([]); // net gekoppeld: weg uit de lijst, ook voor het overzicht ververst is
  const fromList = start === null;
  const todo = unknown.filter((x) => !done.includes(x.domain));
  const u = domain ? unknown.find((x) => x.domain === domain) : null;
  const sugg = domain ? suggestKlant(domain, klanten) : null;
  const choice = sel ?? sugg?.id ?? null;

  const refresh = () => { qc.invalidateQueries({ queryKey: ["support"] }); qc.invalidateQueries({ queryKey: ["domain-links"] }); };
  const pick = (d: string) => { setDomain(d); setSel(null); setQ(""); };
  const back = () => { if (fromList && domain) { setDomain(null); setSel(null); setQ(""); } else onClose(); };
  const save = async () => {
    if (!domain || !choice) return;
    const pid = choice === IGNORE ? null : choice, d = domain;
    const k = klanten.find((x) => x.id === pid);
    setBusy(true);
    try {
      await supportApi.link(d, pid);
      refresh();
      setDone((x) => [...x, d]);
      const undo = () => supportApi.unlink(d).then(() => {
        refresh();
        setDone((x) => x.filter((y) => y !== d));
        toast.flash("Ongedaan gemaakt: " + d + " is weer onbekend");
      }, (e: Error) => toast.flash(e.message, { error: true }));
      toast.flash(pid ? d + " gekoppeld aan klant " + (k?.name || "") : d + " genegeerd: telt niet mee per klant", { undo });
      if (fromList && todo.length > 1) { setDomain(null); setSel(null); setQ(""); } else onClose();
    } catch (e) {
      toast.flash((e as Error).message, { error: true });
    } finally {
      setBusy(false);
    }
  };

  const qq = q.trim().toLowerCase();
  const sorted = [...klanten].sort((a, b) => a.name.localeCompare(b.name));
  const options = [
    ...(sugg ? [{ key: sugg.id, label: sugg.name, note: "voorstel op naam", pill: "Voorstel" }] : []),
    ...sorted.filter((k) => k.id !== sugg?.id).map((k) => ({ key: k.id, label: k.name, note: "", pill: "" })),
  ].filter((o) => !qq || o.label.toLowerCase().includes(qq))
    .concat([{ key: IGNORE, label: "Geen klant", note: "platform of leverancier; telt niet mee per klant", pill: "" }]);

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="kt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Maildomein koppelen</p><h2 id="kt">{domain || "Onbekende afzenderdomeinen"}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className="ex-pill ex-pill--warn"><span className="ex-dot" />{domain ? "Nog geen klant" : nOf(todo.length, "domein", "domeinen") + " zonder klant"}</span>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          {!domain && (
            <div className="p-fs"><h3>Kies een domein</h3>
              <div className="p-capc">
                {todo.map((x) => (
                  <button key={x.domain} type="button" className="p-cand" onClick={() => pick(x.domain)}>
                    <span className="p-av sq">?</span>
                    <span><b>{x.domain}</b><span className="p-capc-n t-mute">{nOf(x.conversations, "gesprek", "gesprekken")}{x.open ? " · " + x.open + " open" : ""} · laatst {when(x.lastReceived).slice(3, 8)}</span></span>
                    <span className="p-capc-go">Klant kiezen</span>
                  </button>
                ))}
                {!todo.length && <p className="p-empty">Alle afzenderdomeinen horen bij een klant.</p>}
              </div>
              <p className="p-kd-note" style={{ margin: "12px 0 0" }}>Een supportgesprek telt voor de klant van het afzenderdomein, ook een subdomein ervan.</p>
            </div>
          )}
          {domain && <>
            <div className="p-fs">
              <div className="p-kv"><span>Gesprekken</span><b>{u ? nOf(u.conversations, "gesprek", "gesprekken") + (u.open ? ", " + u.open + " open" : "") : "–"}</b></div>
              <div className="p-kv"><span>Voorbeeld</span><b>afzenders van {domain}</b></div>
            </div>
            <div className="p-fs"><h3>Kies de klant</h3>
              {klanten.length > 8 && <label className="ex-search p-kp-q"><Icon name="search" size={14} /><input type="text" placeholder="Zoek in de lijst" aria-label="Zoek in de lijst" value={q} onChange={(e) => setQ(e.target.value)} /></label>}
              <div className="p-capc">
                {options.map((o) => (
                  <button key={o.key} type="button" className={"p-cand" + (choice === o.key ? " on" : "")} onClick={() => setSel(o.key)}>
                    <span className={"p-radio" + (choice === o.key ? " on" : "")} />
                    <span className="p-kp-opt-t"><b>{o.label}</b>{o.note && <span className="m">{o.note}</span>}</span>
                    <span>{o.pill && <span className="ex-pill ex-pill--ok">{o.pill}</span>}</span>
                  </button>
                ))}
              </div>
              <p className="p-kd-note" style={{ margin: "12px 0 0" }}>De keuze geldt ook voor gesprekken die al binnen zijn: de telling per klant rekent opnieuw.</p>
            </div>
          </>}
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>De app bewaart de koppeling, met naam en tijd</span>
          <span style={{ display: "flex", gap: 8 }}>
            {domain
              ? <><button type="button" className="ex-btn" onClick={back}>{fromList ? "Terug" : "Annuleren"}</button>
                  <button type="button" className="ex-btn ex-btn--primary" disabled={!choice || busy} onClick={save}><Icon name="check" size={15} />Opslaan</button></>
              : <button type="button" className="ex-btn" onClick={onClose}>Sluiten</button>}
          </span>
        </div>
      </aside>
    </>
  );
}
